<template>
  <v-card :key="currentEtar.tarId" flat>
    <v-card-text>
      <div v-if="!loadingTable">
        <JcdcHeadline
          size="h2"
          :title="currentEtar.tarDesc"
          :subtitle="currentEtar.tarCode"
        >
          <JcdcLabeledText :items="setEtarDetails(currentEtar)" class="py-2" />
        </JcdcHeadline>
        <!-- Buttons -->
        <div class="my-3 d-flex justify-space-between align-center">
          <div>
            <JcdcBtn
              v-if="!alumniUser"
              small
              color="accent"
              :icon="
                needsSignoffFilter
                  ? 'mdi-format-list-bulleted'
                  : 'mdi-format-list-checks'
              "
              @click="needsSignoffFilter = !needsSignoffFilter"
            >
              Show&nbsp;
              <span v-if="needsSignoffFilter">Entire E-TAR</span>
              <span v-else>Unacknowledged</span>
            </JcdcBtn>
          </div>
          <div class="d-flex justify-end align-center">
            <JcdcExportPrintReport
              :app-title="$store.state.appTitle"
              title="ETAR Report"
              align="right"
            >
              <EtarReport
                :report-data="tableData"
                :current-item="currentEtar"
              />
            </JcdcExportPrintReport>
          </div>
        </div>
      </div>
      <JcdcDataTable
        :headers="alumniUser ? tableHeaderAlumni : tableHeader"
        :items="tableData"
        :loading="loadingTable"
        :dense="tableData.length > 50"
        disable-sort
        show-all
        :item-key="currentEtar.tarDetailId"
        no-data-text="There are no E-TARs associated with this course."
        no-results-text='There are no ETARs currently in need of approval. Click "Show Entire E-TAR" above to see the full list of ETARs for this course.'
      >
        <!-- Staff column -->
        <template #[`item.ratingCd`]="{ item }">
          <div class="d-flex align-center">
            <JcdcRatings
              v-if="item.ratedFlag == 'Y'"
              :key="item.tarDetailId"
              :value="item.ratingCd"
              :disabled="!item.ratingCd"
              readonly
              :length="3"
            />
            <JcdcCommentModal
              :title="item.tarDetailDescNoHtmlTags"
              :comment="item.staffComments"
              readonly
            />
          </div>
        </template>
        <!-- Student column -->
        <template #[`item.studentSignoff`]="{ item, index }">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <JcdcCheckboxSimple
                v-if="!disabled && item.ratingCd"
                :check="item.editedSignoff"
                :disabled="!!item.studentSignoff"
                @input="editStudentSignoff(item, $event, index)"
              />
              <v-icon v-else-if="item.editedSignoff">mdi-check-bold</v-icon>
              <span v-if="item.editedSignoffDt" class="ml-3">{{
                formatDisplayDate(item.editedSignoffDt)
              }}</span>
            </div>
            <JcdcCommentModal
              v-if="!disabled && item.ratingCd && !xsScreen"
              class="ml-6"
              :title="item.tarDetailDescNoHtmlTags"
              :comment="item.studComments"
              :edited="item.editedComment"
              @save="editComment(item, $event, index)"
            />
          </div>
        </template>
        <!-- Duty Task column -->
        <template #[`item.tarDetailDescNoHtmlTags`]="{ item }">
          <span :class="styleTarDescription(item)">
            {{ item.tarDetailDescNoHtmlTags }}
            <JcdcBadge
              v-if="item.optional == 'Y'"
              color="secondary lighten-1"
              class="pl-1"
            >
              Optional
            </JcdcBadge>
            <!-- TAR Attribute -->
            <v-tooltip
              v-if="item.tarDetAttribCd == 4742 || item.tarDetAttribCd == 7491"
              top
              max-width="400px"
            >
              <template #activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <JcdcBadge v-if="item.tarDetAttribCd == 4742" color="success">
                    Green
                  </JcdcBadge>
                  <JcdcBadge
                    v-else-if="item.tarDetAttribCd == 7491"
                    color="success darken-1"
                  >
                    NGBS
                  </JcdcBadge>
                </span>
              </template>
              <span>
                <span v-if="item.tarDetAttribCd == 4742">
                  Development and implementation of measures to meet Federal
                  mandates such as, resource conservation, green initiatives and
                  other environmental issues.
                </span>
                <span v-else-if="item.tarDetAttribCd == 7491"
                  >National Green Building Standard</span
                >
              </span>
            </v-tooltip>
          </span>
        </template>
        <!-- Footer -->
        <template #footer>
          <div v-if="!alumniUser" class="mt-6 d-flex justify-end align-center">
            <JcdcBtn
              icon="mdi-content-save"
              align="right"
              icon-right
              :disabled="!edited"
              small
              @click="saveTable()"
            >
              Save Changes
            </JcdcBtn>
          </div>
        </template>
      </JcdcDataTable>
    </v-card-text>
    <JcdcFlashConfirm ref="etarConfirm" />
  </v-card>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import { formatDateTimeValue, formatDisplayDate } from '@/util/formatters'
import EtarReport from '@/views/etar/etarReport.vue'
import {
  JcdcBadge,
  JcdcBtn,
  JcdcCheckboxSimple,
  JcdcCommentModal,
  JcdcDataTable,
  JcdcExportPrintReport,
  JcdcFlashConfirm,
  JcdcHeadline,
  JcdcLabeledText,
  JcdcRatings,
} from '@/components/jcdc/index'

export default {
  name: 'EtarTable',
  components: {
    EtarReport,
    JcdcBadge,
    JcdcBtn,
    JcdcCheckboxSimple,
    JcdcCommentModal,
    JcdcDataTable,
    JcdcExportPrintReport,
    JcdcFlashConfirm,
    JcdcHeadline,
    JcdcLabeledText,
    JcdcRatings,
  },
  beforeRouteUpdate(to, from, next) {
    this.routeGuard(to, from, next)
  },
  beforeRouteLeave(to, from, next) {
    this.routeGuard(to, from, next)
  },
  props: {
    etarDetId: { type: [Number, String], required: true },
  },
  data() {
    return {
      disabled: false,
      alumniUser: this.$store.state.userInfo.userRole.includes(3),
      // ETAR filter
      needsSignoffFilter: false,
      // Report
      etarReportUrl: '/reports/e-tar',
      // TABLE
      tableHeader: [
        {
          text: 'Rating',
          value: 'ratingCd',
          groupable: false,
          sortable: false,
          filter: (v) => {
            if (!this.needsSignoffFilter) return true
            return v > 0
          },
        },
        {
          text: 'Tasks',
          value: 'tarDetailDescNoHtmlTags',
        },
        {
          text: 'Acknowledge/ Comment',
          value: 'studentSignoff',
          filter: (v) => {
            if (!this.needsSignoffFilter) return true
            return v != 'Y'
          },
        },
      ],
      tableHeaderAlumni: [
        {
          text: 'Rating',
          value: 'ratingCd',
        },
        {
          text: 'Tasks',
          value: 'tarDetailDescNoHtmlTags',
        },
      ],
      // Modal
      EditedCurrentEtar: {},
      // Editing/Saving
      currentItem: this.currentEtar,
    }
  },
  computed: {
    ...mapState('etar', [
      'tableData',
      'loadingTable',
      'currentEtar',
      'etarList',
      'edited',
      'updatedTableData',
    ]),
    // Calculate mobile screen
    xsScreen() {
      return this.$vuetify.breakpoint.xsOnly
    },
    // Calculate total ETAR Count (exclude optional)
    etarTotalCount() {
      return this.tableData.filter(
        (x) => x.ratedFlag == 'Y' && x.optional == 'N'
      ).length
    },
  },
  watch: {
    $route() {
      this.$store.commit('etar/SET_ETAR_EDITED', false)
      const updatedEtar = this.etarList.filter(
        (x) => x.enrTarId == this.etarDetId
      )
      this.$store.commit('etar/SET_CURRENT_ETAR', updatedEtar[0])
      if (!this.currentEtar) this.currentEtar = updatedEtar[0]
      this.getEtarDetails(this.currentEtar)
    },
  },
  methods: {
    formatDateTimeValue,
    ...mapActions('etar', ['getEtarDetails', 'saveTable']),
    formatDisplayDate,
    // Editing Functions - MN
    updateEditedCurrentEtar() {
      this.$store.commit('etar/SET_ETAR_EDITED', true)
      this.EditedCurrentEtar = {
        // Note that key is studSignOff not studentSignoff in /UpdateStudPortalTar
        ...this.EditedCurrentEtar,
        studSignOff: this.EditedCurrentEtar.editedSignoff,
        studSignOffDt: this.EditedCurrentEtar.editedSignoffDt,
        studentSignoff: this.EditedCurrentEtar.editedSignoff,
        studentSignoffDt: this.EditedCurrentEtar.editedSignoffDt,
      }
      const findIndex = this.updatedTableData.findIndex(
          (x) => x.tarDetailId === this.EditedCurrentEtar.tarDetailId
        ),
        newTableArray = this.updatedTableData
      if (findIndex == -1) {
        newTableArray.push(this.EditedCurrentEtar)
      } else {
        newTableArray[findIndex] = this.EditedCurrentEtar
      }
      this.$store.commit('etar/SET_ETAR_UPDATED_TABLE', newTableArray)
      this.EditedCurrentEtar = {}
    },
    editComment(item, newComment, index) {
      this.EditedCurrentEtar = item
      this.EditedCurrentEtar.studComments = newComment
      this.EditedCurrentEtar.editedComment = true
      this.updateEditedCurrentEtar(index)
    },
    editStudentSignoff(item, checked, index) {
      this.EditedCurrentEtar = item
      if (checked) {
        this.EditedCurrentEtar.editedSignoff = 'Y'
        this.EditedCurrentEtar.editedSignoffDt = formatDateTimeValue(new Date())
      } else {
        this.EditedCurrentEtar.editedSignoff = null
        this.EditedCurrentEtar.editedSignoffDt = ''
      }
      this.updateEditedCurrentEtar(index)
    },
    // Table styling - MN
    styleTarDescription(item) {
      // FIXME: Data Issue - eTARs have mistakes in the parentId that are affecting formatting. - MN
      // Format description based on outline level - MN
      if (this.tableData.length > 1) {
        if (item.parentId == 0) return 'text-h4 pt-2 pb-1 d-inline-block'
        else if (item.tarChildLevel > 1)
          return `text--secondary text--darken1 pl-${
            item.tarChildLevel + 2
          } d-inline-block`
      }
    },
    // Etar Details - MN
    setEtarDetails(item) {
      // Format detail data for Etar subheader component - MN
      const tarData = {
          'Start Date': this.formatDisplayDate(item.beginDt),
          'End Date': this.formatDisplayDate(item.endDt),
          'Days in E-TAR': item.daysInTar,
          Status: item.tarStatus,
          Center: item.centerName,
          Counselor: item.staffName,
        },
        studentTar = {
          'E-TAR Count': {
            Required: this.etarTotalCount,
            Rated: this.currentEtar.nbrItemsStaffSignoff,
            Signed: this.currentEtar.nbrItemsStudSignoff,
          },
          'Percent Complete': {
            Staff: `${this.currentEtar.ratingPercComp}%`,
            Student: `${this.currentEtar.studRatingPercComp}%`,
            Overall: `${this.currentEtar.overallPercComp}%`,
          },
        },
        alumniTar = {
          'Percent Complete': `${this.currentEtar.ratingPercComp}%`,
        }
      if (!this.alumniUser) return { ...tarData, ...studentTar }
      else if (this.currentEtar.tarStatus != 'Completer')
        return { ...tarData, ...alumniTar }
      return tarData
    },
    routeGuard(to, from, next) {
      if (this.edited) {
        this.$refs.etarConfirm
          .show({
            message: `Are you sure you want exit this page?
              You have unsaved changes.`,
            okButton: 'Exit Without Saving',
            cancelButton: 'Keep Editing',
          })
          .then((ok) => {
            if (ok) next()
            else next(false)
          })
      } else {
        next()
      }
    },
  },
}
</script>
